import { LoadingSmall } from "@/components/LoadingSmall";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { showApiError, showSuccess } from "@/lib/toast";
import { ContentFolder } from "@/lib/types";
import { deleteContentFolder, getContent } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

interface Props {
  onClose: () => void;
  folder: ContentFolder;
  onSuccessfullyDeleted: () => void;
}

export const DeleteFolderDialog: React.FC<Props> = ({ onClose, folder, onSuccessfullyDeleted }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);

  const contentsUnderFolder = useQuery({
    queryKey: ["contentsUnderFolder"],
    queryFn: () =>
      getContent(
        {
          numberOfObjectsPerPage: 10000,
          pageNumber: 1,
        },
        { FirmGuid: String(activeFirm?.firmGuid), FolderGuid: folder.id }
      ),
  });

  const queryClient = useQueryClient();
  const deleteMutation = useMutation({
    mutationFn: () => deleteContentFolder(String(activeFirm?.firmGuid), folder.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contentFolder"] });
      queryClient.invalidateQueries({ queryKey: ["contentsUnderFolder"] });
      showSuccess(t("common.deletedSuccess"));
      onClose();
      onSuccessfullyDeleted();
    },
    onError: showApiError,
  });

  const hasContentsUnderFolder =
    contentsUnderFolder.data?.data?.result && contentsUnderFolder.data.data.result.length > 0;

  return (
    <AlertDialog open onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("Delete folder?")}</AlertDialogTitle>
          <AlertDialogDescription>
            {hasContentsUnderFolder && (
              <Alert variant="info">
                <AlertDescription>{t("folder.deleteFolderDesc")}</AlertDescription>
              </Alert>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("common.dismiss")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              deleteMutation.mutate();
            }}
            disabled={deleteMutation.isPending || contentsUnderFolder.isLoading || hasContentsUnderFolder}
          >
            {t("common.confirm")} {deleteMutation.isPending && <LoadingSmall />}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
